import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Shield,
  DollarSign,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Building2,
  Calculator,
  Clock,
  Target,
  Activity,
  PieChart,
  BarChart3,
  Download,
  Filter,
} from "lucide-react";
import { securitfeeAPI, formatCurrency } from "../services/api";
import PDFExporter from "../utils/pdfExporter";
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from "../utils/reportPeriod";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

// Import operator logos
import unitelLogo from "../assets/logos/unitellogo.png";
import ltcLogo from "../assets/logos/ltclogo.png";
import tplusLogo from "../assets/logos/tpluslogo.png";
import etlLogo from "../assets/logos/etllogo.png";
import bestLogo from "../assets/logos/bestlogo.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// Operator brand colors, matched by code/name (falls back to API color, then slate).
const OPERATOR_BRAND_COLORS = {
  UNITEL: '#ea580c', UNI: '#ea580c',
  LTC: '#dc2626',
  TPLUS: '#d97706', TPL: '#d97706', 'T-PLUS': '#d97706',
  ETL: '#0284c7',
  BEST: '#2563eb', BST: '#2563eb',
};
const getOperatorBrandColor = (operator) => {
  const keys = [operator?.code, operator?.name].map((v) => String(v || '').toUpperCase().trim());
  for (const k of keys) {
    if (OPERATOR_BRAND_COLORS[k]) return OPERATOR_BRAND_COLORS[k];
  }
  const raw = operator?.color || '';
  return /^#[0-9a-fA-F]{8}$/.test(raw) ? raw.slice(0, 7) : (raw || '#64748b');
};

const SecurityFeeDashboard = ({
  appliedMonth: appliedMonthProp,
  appliedYear: appliedYearProp,
}) => {
  const { t } = useTranslation();
  const appliedMonth = appliedMonthProp ?? DEFAULT_REPORT_MONTH;
  const appliedYear = appliedYearProp ?? DEFAULT_REPORT_YEAR;

  const [operatorData, setOperatorData] = useState([]);
  const [subscriberData, setSubscriberData] = useState([]);
  const [overviewData, setOverviewData] = useState({});
  const [monthlyData, setMonthlyData] = useState({
    subscribers: [],
    revenue: [],
  });
  const [selectedPeriod, setSelectedPeriod] = useState("current");

  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const operatorLogos = useMemo(
    () => ({
      UNITEL: unitelLogo,
      LTC: ltcLogo,
      TPLUS: tplusLogo,
      ETL: etlLogo,
      BEST: bestLogo,
    }),
    [],
  );

  const operatorColors = useMemo(
    () => ({
      UNITEL: "#f59e0b",
      LTC: "#ef4444",
      TPLUS: "#8b5cf6",
      ETL: "#3b82f6",
      BEST: "#1c066bff",
    }),
    [],
  );

  // Month options for filtering
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Fetch when parent's applied month/year or local period changes
  useEffect(() => {
    fetchSecurityFeeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedMonth, appliedYear, selectedPeriod]);

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const selectedMonthName =
        months.find((m) => m.value === appliedMonth)?.label || "Current";
      const filename = `security-fee-dashboard-${selectedMonthName.toLowerCase()}`;

      const result = await PDFExporter.exportToPDF(
        "security-fee-dashboard",
        filename,
        {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        },
      );

      if (result.success) {
        // Success notification could be added here
        console.log("PDF exported successfully");
      } else {
        console.error("PDF export failed:", result.error);
        alert("Failed to export PDF: " + result.error);
      }
    } catch (error) {
      console.error("PDF export error:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchSecurityFeeData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let targetMonth = appliedMonth;
      let targetYear = appliedYear;

      if (selectedPeriod === "previous") {
        targetMonth = appliedMonth - 1;
        if (targetMonth < 1) {
          targetMonth = 12;
          targetYear = appliedYear - 1;
        }
      } else if (selectedPeriod === "quarterly") {
        const quarterIndex = Math.floor((appliedMonth - 1) / 3);
        targetMonth = (quarterIndex + 1) * 3;
      } else if (selectedPeriod === "yearly") {
        targetMonth = 12;
      }

      const params = { month: targetMonth, year: targetYear };

      const summaryResponse = await securitfeeAPI.getSummary(params);

      if (!summaryResponse?.success) {
        throw new Error(summaryResponse?.error || "Summary API returned no data");
      }

      const summary = summaryResponse.data || {};
      setOverviewData(summary);

      const operators = summary.operators || [];
      const operatorFeeData = operators;

      const processedOperatorData = Array.isArray(operators)
        ? operators.map((operator) => {
            const feeData = Array.isArray(operatorFeeData)
              ? operatorFeeData.find(
                  (op) =>
                    op.operator_id === operator.operator_id ||
                    op.operatorName === operator.operator_name ||
                    op.operatorCode === operator.operator_code ||
                    op.operatorCode === operator.operator_name,
                )
              : null;

            const code = (operator.operator_code || "").toUpperCase();
            const name = operator.operator_name || "";

            return {
              id: operator.operator_id,
              name: name,
              code: operator.operator_code || code,
              logo: operatorLogos[code] || operatorLogos[name] || operatorLogos[name.toUpperCase()],
              color: operatorColors[code] || operatorColors[name] || "#6b7280",
              expectedAmount: parseFloat(feeData?.expectedAmount || 0),
              collectedAmount: parseFloat(feeData?.collectedAmount || 0),
              pendingAmount: parseFloat(feeData?.pendingAmount || 0),
              overdueAmount: parseFloat(feeData?.overdueAmount || 0),
              collectionRate: parseFloat(feeData?.collectionRate || 0),
              remaining: parseFloat(feeData?.remaining || 0),
              transactionCount: parseInt(feeData?.transactionCount || 0),
              subscribers: parseInt(operator.total_subscribers) || 0,
              activeSubscribers: parseInt(operator.active_subscribers) || 0,
            };
          })
        : [];

      setOperatorData(processedOperatorData);

      // generate monthly data as before
      const generateMonthlyData = () => {
        const monthsArr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const currentMonthIndex = new Date().getMonth();
        const last12 = [];
        for (let i = 11; i >= 0; i--) {
          const idx = (currentMonthIndex - i + 12) % 12;
          last12.push(monthsArr[idx]);
        }
        const subscriberTrend = last12.map((m, i) => 9.2 + i * 0.1);
        const revenueTrend = last12.map((m, i) => 38.5 + Math.sin((i/12)*Math.PI*2)*2 + i*0.5);
        return { subscribers: last12.map((month, i) => ({ month, value: subscriberTrend[i] })), revenue: last12.map((month, i) => ({ month, value: revenueTrend[i] })) };
      };

      setMonthlyData(generateMonthlyData());

      // processed subscriber data (kept for potential use but not rendered here)
      const processedSubscriberData = Array.isArray(operators)
        ? operators.map((operator) => {
            const code = (operator.operator_code || "").toUpperCase();
            const name = operator.operator_name || "";
            return {
              id: operator.operator_id,
              name,
              code,
              logo: operatorLogos[code] || operatorLogos[name] || operatorLogos[name.toUpperCase()],
              color: operatorColors[code] || operatorColors[name] || "#6b7280",
              subscribers: parseInt(operator.total_subscribers) || 0,
              activeSubscribers: parseInt(operator.active_subscribers) || 0,
              compliance: parseFloat(operator.avg_compliance_rate) || 0,
            };
          })
        : [];

      setSubscriberData(processedSubscriberData);
    } catch (err) {
      console.error("Error fetching security fee data:", err);
      setError("Failed to load security fee data: " + (err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [appliedMonth, appliedYear, selectedPeriod, operatorLogos, operatorColors]);

  const formatKip = formatCurrency;

  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    return new Intl.NumberFormat("en-US").format(parseInt(num));
  };

  const getComplianceStatus = (rate) => {
    if (rate >= 95)
      return {
        status: "excellent",
        color: "green",
        text: t("securityFeeDashboard.compliance.excellent"),
        bgColor: "bg-green-100",
        textColor: "text-green-800",
      };
    if (rate >= 90)
      return {
        status: "good",
        color: "blue",
        text: t("securityFeeDashboard.compliance.good"),
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
      };
    if (rate >= 80)
      return {
        status: "warning",
        color: "yellow",
        text: t("securityFeeDashboard.compliance.needsReview"),
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-800",
      };
    return {
      status: "critical",
      color: "red",
      text: t("securityFeeDashboard.compliance.critical"),
      bgColor: "bg-red-100",
      textColor: "text-red-800",
    };
  };

  const calculateTotals = () => {
    return operatorData.reduce(
      (totals, operator) => ({
        totalExpected: totals.totalExpected + operator.expectedAmount,
        totalCollected: totals.totalCollected + operator.collectedAmount,
        totalPending: totals.totalPending + operator.pendingAmount,
        totalOverdue: totals.totalOverdue + operator.overdueAmount,
        totalRemaining: totals.totalRemaining + operator.remaining,
        totalTransactions: totals.totalTransactions + operator.transactionCount,
      }),
      {
        totalExpected: 0,
        totalCollected: 0,
        totalPending: 0,
        totalOverdue: 0,
        totalRemaining: 0,
        totalTransactions: 0,
      },
    );
  };

  const totals = calculateTotals();
  const overallCollectionRate =
    totals.totalExpected > 0
      ? (totals.totalCollected / totals.totalExpected) * 100
      : 0;

  const collectionRateChartData = {
    labels: operatorData.map((op) => op.code),
    datasets: [
      {
        label: "Collection Rate (%)",
        data: operatorData.map((op) => op.collectionRate),
        backgroundColor: operatorData.map((op) => op.color),
        borderColor: operatorData.map((op) => op.color),
        borderWidth: 1,
      },
    ],
  };

  const collectionAmountChartData = {
    labels: operatorData.map((op) => op.code),
    datasets: [
      {
        label: "Collected",
        data: operatorData.map((op) => op.collectedAmount / 1000000), // Convert to millions
        backgroundColor: "#10b981",
        borderColor: "#10b981",
        borderWidth: 1,
      },
      {
        label: "Remaining",
        data: operatorData.map((op) => op.remaining / 1000000), // Convert to millions
        backgroundColor: "#ef4444",
        borderColor: "#ef4444",
        borderWidth: 1,
      },
    ],
  };

  const overallDistributionData = {
    labels: [t("common.collected"), t("common.pending"), t("securityFeeDashboard.remainCollection")],
    datasets: [
      {
        data: [
          totals.totalCollected,
          totals.totalPending,
          totals.totalRemaining,
        ],
        backgroundColor: ["#10b981", "#f59e0b", "#f97316"],
        borderWidth: 2,
        borderColor: "#ffffff",
      },
    ],
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">
            {t("securityFeeDashboard.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">
                  {t("common.errorLoadingData")}
                </h3>
                <p className="text-red-600 mt-1">{error}</p>
                <button
                  onClick={fetchSecurityFeeData}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  {t("common.retry")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="security-fee-dashboard" className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600 mr-4" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {t("securityFeeDashboard.title")}
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                  {t("securityFeeDashboard.subtitle")}
                </p>
              </div>
            </div>
            {/* Period Filter (month/year come from parent dashboard Search) */}
            {/* <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <label className="text-sm text-gray-600">Period:</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="current">Current Month</option>
                  <option value="previous">Previous Month</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div> */}

            {/* Action Buttons Container */}
            {/* Export Button */}
            {/* <div className="flex items-center gap-2 mt-2 sm:mt-0 w-full sm:w-auto">
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className={`flex-1 sm:flex-none flex justify-center items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                  isExporting
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                }`}
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {isExporting ? "Exporting..." : "Export PDF"}
                </span>
                <span className="sm:hidden">{isExporting ? "..." : "PDF"}</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <Calculator className="h-10 w-10 text-blue-200 mb-2" />
            <p className="text-blue-100 text-sm">{t("securityFeeDashboard.totalExpected")}</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.overview?.totalEstimate || totals.totalExpected,
              )}
            </p>
            <p className="text-blue-100 text-xs mt-2">
              {t("securityFeeDashboard.transactions", {
                value: formatNumber(
                  overviewData?.summary?.totalTransactions ||
                    totals.totalTransactions,
                ),
              })}
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <CheckCircle className="h-10 w-10 text-green-200 mb-2" />
            <p className="text-green-100 text-sm">{t("common.collected")}</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.overview?.totalCollected || totals.totalCollected,
              )}
            </p>
            <p className="text-green-100 text-xs mt-2">
              {t("securityFeeDashboard.collectionRateSuffix", {
                value: (
                  overviewData?.overview?.collectionRate || overallCollectionRate
                ).toFixed(1),
              })}
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <Clock className="h-10 w-10 text-yellow-200 mb-2" />
            <p className="text-yellow-100 text-sm">{t("common.pending")}</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.breakdown?.pending || totals.totalPending,
              )}
            </p>
            <p className="text-yellow-100 text-xs mt-2">
              {overviewData?.overview?.totalEstimate > 0
                ? (
                    (overviewData.breakdown.pending /
                      overviewData.overview.totalEstimate) *
                    100
                  ).toFixed(1)
                : totals.totalExpected > 0
                  ? (
                      (totals.totalPending / totals.totalExpected) *
                      100
                    ).toFixed(1)
                  : 0}
              {t("securityFeeDashboard.ofTotalSuffix")}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Collection Rate Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            {t("securityFeeDashboard.chartCollectionRate")}
          </h3>
          <div className="h-64">
            <Bar
              data={collectionRateChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                      callback: function (value) {
                        return value + "%";
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Collection Amount Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            {t("securityFeeDashboard.chartCollectionAmount")}
          </h3>
          <div className="h-64">
            <Bar
              data={collectionAmountChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top" } },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        </div>

        {/* Overall Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2" />
            {t("securityFeeDashboard.chartOverallDistribution")}
          </h3>
          <div className="h-64">
            <Doughnut
              data={overallDistributionData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "bottom" },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `${context.label}: ${formatKip(context.raw)}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Operator Subscribers Section — hidden */}
      {false && (
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-2" />
              Operator Subscribers
            </h2>
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm text-gray-600">Total:</span>
                <span className="ml-2 text-lg font-bold text-gray-900">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) => total + op.subscribers,
                      0,
                    ),
                  )}
                </span>
              </div>
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">Active Rate:</span>
                <span className="ml-2 text-lg font-bold text-green-600">
                  {subscriberData.length > 0 &&
                  subscriberData.reduce(
                    (total, op) => total + op.subscribers,
                    0,
                  ) > 0
                    ? (
                        (subscriberData.reduce(
                          (total, op) => total + op.activeSubscribers,
                          0,
                        ) /
                          subscriberData.reduce(
                            (total, op) => total + op.subscribers,
                            0,
                          )) *
                        100
                      ).toFixed(1)
                    : "0.0"}
                  %
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {subscriberData.map((operator, index) => {
              const totalSubscribers = subscriberData.reduce(
                (total, op) => total + op.subscribers,
                0,
              );
              const marketShare =
                totalSubscribers > 0
                  ? (operator.subscribers / totalSubscribers) * 100
                  : 0;
              const activeRate =
                operator.subscribers > 0
                  ? (operator.activeSubscribers / operator.subscribers) * 100
                  : 0;

              return (
                <div
                  key={operator.id || operator.name || index}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200 border border-blue-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center p-2 border border-gray-200">
                      {operator.logo ? (
                        <img
                          src={operator.logo}
                          alt={`${operator.name} logo`}
                          className="w-10 h-10 object-contain "
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-10 h-10 rounded-lg ${!operator.logo ? "flex" : "hidden"} items-center justify-center`}
                        style={{ backgroundColor: operator.color }}
                      >
                        <span className="text-white text-sm font-bold ">
                          {operator.name && operator.name.length > 0
                            ? operator.name.charAt(0)
                            : "?"}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                      {marketShare.toFixed(1)}%
                    </span>
                  </div>

                  <div className="text-center mb-3">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {operator.name || "Unknown"}
                    </h3>
                    <p
                      className="text-2xl font-bold mb-1"
                      style={{ color: operator.color }}
                    >
                      {formatNumber(operator.subscribers)}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">Subscribers</p>
                    <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      <TrendingUp className="h-3 w-3 inline mr-1" />
                      {activeRate.toFixed(1)}% Active
                    </div>
                  </div>

                  {/* Market share progress bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Market Share</span>
                      <span>{marketShare.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${marketShare}%`,
                          backgroundColor: operator.color,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Active vs Inactive subscribers */}
                  <div className="text-xs text-gray-600">
                    <div className="flex justify-between mb-1">
                      <span>
                        Active: {formatNumber(operator.activeSubscribers)}
                      </span>
                      <span>
                        Inactive:{" "}
                        {formatNumber(
                          operator.subscribers - operator.activeSubscribers,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Statistics */}
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
              Subscriber Distribution Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) => total + op.subscribers,
                      0,
                    ),
                  )}
                </div>
                <div className="text-sm text-blue-800 font-medium">
                  Total Subscribers
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) => total + op.activeSubscribers,
                      0,
                    ),
                  )}
                </div>
                <div className="text-sm text-green-800 font-medium">
                  Active Subscribers
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) =>
                        total + (op.subscribers - op.activeSubscribers),
                      0,
                    ),
                  )}
                </div>
                <div className="text-sm text-gray-800 font-medium">
                  Inactive Subscribers
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">
                  {subscriberData.length}
                </div>
                <div className="text-sm text-indigo-800 font-medium">
                  Total Operators
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Operator Performance Cards */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Building2 className="h-6 w-6 mr-2" />
          {t("securityFeeDashboard.performanceDetails")}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {operatorData.map((operator) => {
            const compliance = getComplianceStatus(operator.collectionRate);
            const brandColor = getOperatorBrandColor(operator);

            return (
              <div
                key={operator.id}
                className="bg-white rounded-lg shadow border border-gray-200 border-t-4 p-6"
                style={{ borderTopColor: brandColor }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {operator.logo && (
                      <img
                        src={operator.logo}
                        alt={operator.name}
                        className="w-12 h-12 object-contain"
                      />
                    )}
                    <div>
                      <h3 className="text-xl font-semibold" style={{ color: brandColor }}>
                        {operator.name}
                      </h3>
                      <p className="text-sm text-gray-600">{operator.code}</p>
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${compliance.bgColor} ${compliance.textColor}`}
                  >
                    {compliance.text}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      {t("securityFeeDashboard.expectedCollection")}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatKip(operator.expectedAmount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t("securityFeeDashboard.transactions", { value: formatNumber(operator.transactionCount) })}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      {t("securityFeeDashboard.amountCollected")}
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {formatKip(operator.collectedAmount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {operator.collectionRate.toFixed(1)}{t("securityFeeDashboard.collectedSuffix")}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">{t("common.pending")}</div>
                    <div className="text-lg font-bold text-yellow-600">
                      {formatKip(operator.pendingAmount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {operator.expectedAmount > 0
                        ? (
                            (operator.pendingAmount / operator.expectedAmount) *
                            100
                          ).toFixed(1)
                        : 0}
                      {t("securityFeeDashboard.pendingSuffix")}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      {t("securityFeeDashboard.remainCollection")}
                    </div>
                    <div className="text-lg font-bold text-orange-600">
                      {formatKip(operator.remaining)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {operator.expectedAmount > 0
                        ? (
                            (operator.remaining / operator.expectedAmount) *
                            100
                          ).toFixed(1)
                        : 0}
                      {t("securityFeeDashboard.remainingSuffix")}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      {t("securityFeeDashboard.eligibleSubscribers")}
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {formatNumber(operator.collectedAmount / 3000)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      {t("securityFeeDashboard.notEligibleSubscribers")}
                    </div>
                    <div className="text-lg font-bold text-red-600">
                      {formatNumber(operator.remaining / 3000)}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-700 mb-2">
                    <span>{t("securityFeeDashboard.collectionProgress")}</span>
                    <span>{operator.collectionRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="flex h-3 rounded-full overflow-hidden">
                      <div
                        className="bg-green-500 transition-all duration-500"
                        style={{
                          width: `${operator.expectedAmount > 0 ? (operator.collectedAmount / operator.expectedAmount) * 100 : 0}%`,
                        }}
                      ></div>
                      <div
                        className="bg-yellow-400 transition-all duration-500"
                        style={{
                          width: `${operator.expectedAmount > 0 ? (operator.pendingAmount / operator.expectedAmount) * 100 : 0}%`,
                        }}
                      ></div>
                      <div
                        className="bg-orange-500 transition-all duration-500"
                        style={{
                          width: `${operator.expectedAmount > 0 ? (operator.remaining / operator.expectedAmount) * 100 : 0}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>✓ {formatKip(operator.collectedAmount)}</span>
                    <span>⚠ {formatKip(operator.remaining)}</span>
                  </div>
                </div>

                {/* Action required indicator */}
                {operator.collectionRate < 90 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-800">
                          Action Required
                        </div>
                        <div className="text-xs text-red-700 mt-1">
                          Collection rate is below 90%. Immediate follow-up
                          recommended.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Statistics — hidden */}
      {false && (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Target className="h-5 w-5 mr-2" />
          Performance Summary
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {operatorData.filter((op) => op.collectionRate >= 95).length}
            </div>
            <div className="text-sm text-gray-600">Operators Above 95%</div>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {
                operatorData.filter(
                  (op) => op.collectionRate >= 90 && op.collectionRate < 95,
                ).length
              }
            </div>
            <div className="text-sm text-gray-600">Operators 90-95%</div>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {operatorData.filter((op) => op.collectionRate < 90).length}
            </div>
            <div className="text-sm text-gray-600">Operators Below 90%</div>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {overallCollectionRate.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Overall Collection Rate</div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default SecurityFeeDashboard;
